import moment from "moment";
import { ApiClient } from "@/api/ApiClient";
import { EDocumentReportRow } from "@/formTypes/EDocumentReportDataType";
import { EDOCUMENT_TYPE } from "./Constants";
import { formatMoneyValue } from "./Currency";

/** Valor del select cuando no se filtra por tipo. */
export const EDOCUMENT_REPORT_ALL_KINDS = "todos";

/**
 * Tipos de comprobante que el reporte lista, en orden de presentacion del filtro.
 * Las llaves son los codigos de Hacienda de `EDOCUMENT_TYPE`.
 */
export const EDOCUMENT_REPORT_KIND_LABELS: Record<string, string> = {
    [EDOCUMENT_TYPE.Invoice]: "Factura Electronica",
    [EDOCUMENT_TYPE.Ticket]: "Tiquete Electronico",
    [EDOCUMENT_TYPE.ExportInvoice]: "Factura Electronica de Exportacion",
    [EDOCUMENT_TYPE.Creditnote]: "Nota de Credito",
    [EDOCUMENT_TYPE.Debitnote]: "Nota de Debito",
    [EDOCUMENT_TYPE.BuyInvoice]: "Factura Electronica de Compra",
    [EDOCUMENT_TYPE.aceptada]: "Mensaje de Aceptacion",
    [EDOCUMENT_TYPE.aceptada_parcial]: "Mensaje de Aceptacion Parcial",
    [EDOCUMENT_TYPE.rechazada]: "Mensaje de Rechazo",
};

/** Etiqueta del tipo de comprobante, con respaldo por si llega un codigo desconocido. */
export const resolveEDocumentKindLabel = (kind?: string): string =>
    EDOCUMENT_REPORT_KIND_LABELS[`${kind}`] || "Documento Electronico";

/**
 * Filtro de Meili para el listado. El reporte muestra todos los comprobantes sin importar
 * si llegaron a enviarse a Hacienda: los no enviados y los pendientes de respuesta tambien
 * son parte de lo que hay que revisar, y la columna de estado es la que los distingue.
 * El aislamiento por tenant lo agrega el handler `search-api`, no este filtro.
 */
export const buildEDocumentReportFilter = (kind?: string): string => {
    if (!kind || kind === EDOCUMENT_REPORT_ALL_KINDS) {
        return "";
    }
    return `kind = "${kind}"`;
};

/**
 * Ruta de la version impresa segun el modelo de origen. Los mensajes ATV no tienen
 * impresion propia, asi que apuntan al gasto o factura de proveedor que respondieron;
 * si el mensaje no quedo ligado a ninguno, se devuelve cadena vacia y la accion se oculta.
 */
export const resolveEDocumentViewUrl = (row: EDocumentReportRow): string => {
    const { docId } = row;
    if (!docId) return "";

    switch (row.sourceModel) {
        case "Invoice":
            return `/user/invoices/view/${docId}`;
        case "Creditnote":
            return `/user/creditnotes/view/${docId}`;
        case "Debitnote":
            return `/user/debitnotes/view/${docId}`;
        case "BuyInvoice":
            return `/user/buyInvoices/view/${docId}`;
        case "AtvMessage":
            if (row.expenseNoteId && row.expenseId) {
                return `/user/expenses/notes/edit/${row.expenseNoteId}/${row.expenseId}`;
            }
            if (row.expenseId) {
                return `/user/expenses/edit/${row.expenseId}`;
            }
            if (row.providerNoteId && row.providerInvoiceId) {
                return `/user/provider-invoices/notes/edit/${row.providerNoteId}/${row.providerInvoiceId}`;
            }
            if (row.providerInvoiceId) {
                return `/user/provider-invoices/invoice/${row.providerInvoiceId}`;
            }
            return "";
        default:
            return "";
    }
};

/**
 * Fecha del documento en el formato de los listados (DD-MM-YYYY). Los formatos se
 * declaran explicitamente porque el indice mezcla fechas con hora (notas y mensajes ATV)
 * y sin hora (facturas), y el parseo libre de moment emite deprecation warnings.
 */
export const formatEDocumentDate = (fecha?: string): string => {
    if (!fecha) return "";
    const date = moment(fecha, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return date.isValid() ? date.format("DD-MM-YYYY") : "";
};

/** Estado de la respuesta de Hacienda en texto, para el Excel. */
export const formatEDocumentAtvStatus = (row: EDocumentReportRow): string => {
    switch (row.atvStatus) {
        case "aceptado":
            return "Aceptada";
        case "rechazado":
            return "Rechazada";
        default:
            return "Pendiente Respuesta";
    }
};

/** Total con el simbolo de la moneda del documento. */
export const formatEDocumentTotal = (row: EDocumentReportRow): string =>
    formatMoneyValue(Number(row.total || 0), Number(row.currency_id || 0));

/** Encabezados del Excel, en el mismo orden que `buildEDocumentReportRow`. */
export const EDOCUMENT_REPORT_EXCEL_TITLES = [
    "Fecha",
    "Tipo",
    "Consecutivo",
    "Cliente",
    "Total",
    "Estado ATV",
];

/** Fila del Excel. El total va sin formato para que Excel lo trate como numero. */
export const buildEDocumentReportRow = (row: EDocumentReportRow): string[] => [
    formatEDocumentDate(row.fecha),
    resolveEDocumentKindLabel(row.kind),
    `${row.sequence || ""}`,
    `${row.nombre || ""}`,
    `${Number(row.total || 0)}`,
    formatEDocumentAtvStatus(row),
];

/** Nombre del indice unificado en Meilisearch. */
export const EDOCUMENTS_MEILI_INDEX = "edocuments";

/** Orden del reporte: del mas nuevo al mas antiguo. */
export const EDOCUMENT_REPORT_SORT = ["fechaTs:desc"];

/** Tope de paginas que el Excel recorre, para no colgar el navegador en tenants grandes. */
export const EDOCUMENT_REPORT_EXPORT_MAX_PAGES = 200;

/** Maximo que acepta el handler de busqueda (`search-api`). */
const EXPORT_HITS_PER_PAGE = 100;

export type EDocumentReportExportResult = {
    rows: EDocumentReportRow[];
    /** Quedaron documentos sin exportar porque se alcanzo el tope de paginas. */
    truncated: boolean;
};

/**
 * Trae todas las paginas del indice para el filtro activo. El listado pagina de a 30 en
 * pantalla, pero el Excel tiene que llevar el set completo del filtro.
 */
export const fetchAllEDocumentReportRows = async (
    kind?: string,
    maxPages: number = EDOCUMENT_REPORT_EXPORT_MAX_PAGES,
): Promise<EDocumentReportExportResult> => {
    const filter = buildEDocumentReportFilter(kind);
    const rows: EDocumentReportRow[] = [];

    let page = 1;
    let totalPages = 1;

    do {
        const result = await ApiClient.queries.searchGeneric({
            index: EDOCUMENTS_MEILI_INDEX,
            q: "",
            page,
            hitsPerPage: EXPORT_HITS_PER_PAGE,
            sort: EDOCUMENT_REPORT_SORT,
            filter,
        });

        const hits = JSON.parse(`${result.data?.hits || "[]"}`) as EDocumentReportRow[];
        rows.push(...hits);

        totalPages = Number(result.data?.totalPages || 0);
        page += 1;
    } while (page <= totalPages && page <= maxPages);

    return { rows, truncated: totalPages > maxPages };
};
